import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";
import JsonLd from "@/components/base/JsonLd";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CategoryDropdown from "@/components/feature/CategoryDropdown";
import CategoryGrid from "@/components/feature/CategoryGrid";
import ResourceCards from "@/components/feature/ResourceCards";
import ArticleList from "@/components/feature/ArticleList";
import ComparisonTable from "@/components/feature/ComparisonTable";
import FaqAccordion from "@/components/feature/FaqAccordion";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Colorful%20flat%20lay%20of%20stylish%20sneakers%2C%20folded%20hoodies%20and%20caps%20in%20warm%20terracotta%20and%20cream%20tones%20arranged%20on%20a%20rich%20dark%20green%20background%2C%20soft%20dramatic%20lighting%2C%20premium%20fashion%20editorial%20photography%20with%20high%20detail&width=1600&height=900&seq=usfans-hero-01&orientation=landscape&nocache=true";

const SITE_URL = import.meta.env.VITE_SITE_URL;
const LOGO_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "USFansSheet",
      description:
        "USFansSheet is a clean, helpful hub for the USFans Spreadsheet. Browse product categories, read original guides and comparisons, then shop on the main website.",
      inLanguage: ["en", "pl", "es", "fr"],
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "USFansSheet",
      url: `${SITE_URL}/`,
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/`,
      url: `${SITE_URL}/`,
      name: "USFans Spreadsheet: Explore, Compare & Organize Creator Resources",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      primaryImageOfPage: { "@type": "ImageObject", url: HERO_IMAGE },
    },
  ],
};

const features = [
  { icon: "ri-layout-grid-line", title: "features.1.title", text: "features.1.text" },
  { icon: "ri-arrow-left-right-line", title: "features.2.title", text: "features.2.text" },
  { icon: "ri-shield-check-line", title: "features.3.title", text: "features.3.text" },
];

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd} />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative flex min-h-[680px] w-full items-center justify-center overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt={t("site.name")}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>

          <div className="relative z-10 w-full max-w-4xl px-4 py-28 text-center md:px-6">
            <span className="animate-fade-up inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-background-50 backdrop-blur">
              {t("hero.eyebrow")}
            </span>
            <h1 className="animate-fade-up mt-6 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl" style={{ animationDelay: "0.1s" }}>
              {t("hero.title")}
            </h1>
            <p className="animate-fade-up mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background-50/90 md:text-lg" style={{ animationDelay: "0.2s" }}>
              {t("hero.subtitle")}
            </p>

            <div className="animate-fade-up mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" style={{ animationDelay: "0.3s" }}>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                {t("hero.cta.main")}
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 sm:w-auto"
              >
                {t("hero.cta.buy")}
              </a>
              <Link
                to="/usfans-spreadsheet-guide"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 sm:w-auto"
              >
                {t("hero.cta.guide")}
              </Link>
            </div>

            <p className="animate-fade-up mt-8 text-xs text-background-50/70" style={{ animationDelay: "0.4s" }}>
              {t("hero.trust")}
            </p>
          </div>
        </section>

        {/* What is */}
        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("what.title")}</h2>
            <p className="mt-5 text-base leading-relaxed text-foreground-700">{t("what.text1")}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("what.text2")}</p>
            <Link
              to="/usfans-spreadsheet-guide"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("what.cta")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="scroll-mt-24 bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("categories.title")}</h2>
              <p className="mt-3 text-base text-foreground-600">{t("categories.subtitle")}</p>
            </div>

            <div className="mt-8 flex justify-center">
              <CategoryDropdown />
            </div>

            <div className="mt-10">
              <CategoryGrid />
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("resources.title")}</h2>
            <p className="mt-3 text-base text-foreground-600">{t("resources.subtitle")}</p>
          </div>
          <div className="mt-10">
            <ResourceCards />
          </div>
        </section>

        {/* Features */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("features.title")}
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {features.map((f) => (
                <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">{t(f.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(f.text)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("compare.title")}
            </h2>
            <div className="mt-8">
              <ComparisonTable />
            </div>
          </div>
        </section>

        {/* Pillar articles */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t("articles.title")}</h2>
              <p className="mt-3 text-base text-foreground-600">{t("articles.subtitle")}</p>
            </div>
            <div className="mt-10">
              <ArticleList />
            </div>
          </div>
        </section>

        {/* Article promo */}
        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="overflow-hidden rounded-lg bg-primary-50">
            <div className="flex flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row md:px-12">
              <div className="text-center md:text-left">
                <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("articlePromo.title")}</h2>
                <p className="mt-3 max-w-xl text-base leading-relaxed text-foreground-700">{t("articlePromo.text")}</p>
              </div>
              <Link
                to="/usfans-spreadsheet-guide"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("articlePromo.cta")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-24 bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("faq.title")}
            </h2>
            <div className="mt-8">
              <FaqAccordion />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}